import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { SubscriptionPrompt } from './SubscriptionPrompt';

const RiskSection = ({ agent, details }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Helper function to format agent name
  const formatAgentTitle = (agentName) => {
    switch(agentName) {
      case 'pilot_risk':
        return 'Pilot Experience and Licensing';
      case 'physical_risk':
        return 'Physical Conditions';
      case 'psychological_risk':
        return 'Psychological Factors';
      case 'weather_risk':
        return 'Weather Conditions';
      case 'airport_risk':
        return 'Airport and Facilities';
      case 'task_risk':
        return 'Task and Mission';
      case 'final_risk':
        return 'Overall Risk Assessment';
      default:
        return agentName.replace('_', ' ').toUpperCase();
    }
  };

  // Helper function to get risk level color
  const getRiskLevelColor = (riskLevel) => {
    switch(riskLevel?.toLowerCase()) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'extreme':
        return 'bg-red-200 text-red-900';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Extract the relevant details for this section
  const sectionDetails = details[agent];
  const isFinalRisk = agent === 'final_risk';
  
  return (
    <div className={`mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm 
      ${isFinalRisk ? 'border-2 border-blue-500' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {formatAgentTitle(agent)}
          </h3>
          {sectionDetails.risk_level && (
            <span className={`px-2 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(sectionDetails.risk_level)}`}>
              {sectionDetails.risk_level}
            </span>
          )}
        </div>
        {!isFinalRisk && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-gray-500 hover:text-gray-700"
          >
            {expanded ? '▼' : '▶'}
          </button>
        )}
      </div>

      <p className="text-gray-700 mt-2 mb-4">{sectionDetails.summary}</p>
      
      {sectionDetails.subscription_required ? (
        <SubscriptionPrompt 
          type={agent}
          title={`Want detailed ${formatAgentTitle(agent).toLowerCase()} analysis?`}
        />
      ) : (
        <>
          {!isFinalRisk && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              {expanded ? 'Show Less' : 'View More Details'}
            </button>
          )}
          
          {(expanded || isFinalRisk) && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <ReactMarkdown className="prose max-w-none">
                {sectionDetails.details}
              </ReactMarkdown>
              {isFinalRisk && sectionDetails.final_risk && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="font-medium text-gray-900">
                    Final Risk Score: {sectionDetails.final_risk}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RiskSection; 